<template>
    <div class="w-full">
        <SearchBar @update-filters="updateFilters" @clear-filters="clearFilters" @add-click="openAddNewOneDialog"
            @update-advance-filters="updateAdvanceFiltersHandler">
        </SearchBar>
        <!-- UK / USA / Other, with the number of servers in each (after the search boxes are applied) -->
        <div class="region-tabs" role="tablist">
            <button v-for="r in REGIONS" :key="r.key" type="button" role="tab" class="region-tab"
                :class="{ 'is-on': region === r.key }" :aria-selected="region === r.key" @click="region = r.key">
                {{ r.label }} <span class="region-tab__count">{{ regionCounts[r.key] }}</span>
            </button>
        </div>
        <TableView :serverList="regionList" class="border " />
        <div class="unused-foot">
            <span>{{ unusedNote }}</span>
            <button type="button" class="unused-foot__btn" :disabled="checking" @click="checkNow">{{ checking ? 'Checking…' : 'Check now' }}</button>
        </div>
    </div>
    <div>
        <AddNewOneDialog @close="addNewOneDialogFlag = false" />
    </div>
</template>
<script setup>
import { ref, computed, watchEffect } from 'vue';
import { useAdminServerStore } from "@/stores"

import AddNewOneDialog from "./Modals/AddNewOneDialog.vue"

import { storeToRefs } from 'pinia';

import SearchBar from './SearchBar.vue';
// import AddNewOneDialog from "./Modals/AddNewOneDialog.vue"
// import UpdateInfoDialog from "./Modals/UpdateInfoDialog.vue"
import TableView from "./TableView.vue"
const filters = ref({
    searchFilterName: "",
    searchFilterLocation: "",
    searchFilterDomain: ""
})
const updateFilters = (filtername, value) => {
    filters.value[filtername] = value
    serverList.value = servers.value.filter(server => {
        const { searchFilterName, searchFilterLocation, searchFilterDomain } = filters.value;

        const nameFilters = searchFilterName.toLowerCase().split(' ');
        const locationFilters = searchFilterLocation.toLowerCase().split(' ');
        const domainFilters = searchFilterDomain.toLowerCase().split(' ');

        const nameMatches = nameFilters.every(filter => server.modemName.toLowerCase().includes(filter));
        const locationMatches = locationFilters.every(filter => server.country.toLowerCase().includes(filter) || server.city.toLowerCase().includes(filter));
        const domainMatches = domainFilters.every(filter => server.domain.toLowerCase().includes(filter));

        return nameMatches && locationMatches && domainMatches;
    });
};
const clearFilters = () => {
    // Clear the search filter values
    filters.value.searchFilterName = ""
    filters.value.searchFilterLocation = ""
    filters.value.searchFilterDomain = ""
    serverList.value = servers.value
};

// server categories: the page opens on All; "Other" is every country that is not the UK or the USA
const REGIONS = [{ key: 'all', label: 'All' }, { key: 'uk', label: 'UK' }, { key: 'usa', label: 'USA' }, { key: 'other', label: 'Other' }]
const region = ref('all')
const regionOf = (server) => {
    const c = String(server?.country || '').trim().toLowerCase()
    if (['united kingdom', 'uk', 'great britain', 'england'].includes(c)) return 'uk'
    if (['united states', 'usa', 'us', 'united states of america'].includes(c)) return 'usa'
    return 'other'
}
const regionCounts = computed(() => {
    const counts = { uk: 0, usa: 0, other: 0, all: 0 }
    for (const server of serverList.value || []) { counts[regionOf(server)]++; counts.all++ }
    return counts
})
const regionList = computed(() => region.value === 'all' ? serverList.value : (serverList.value || []).filter((server) => regionOf(server) === region.value))

const adminServerStore = useAdminServerStore()
// "Unused" column: when it was last refreshed, and a button to refresh it now (runs in the background)
const checking = ref(false)
const clock = (value) => value ? new Date(value).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : null
const unusedNote = computed(() => {
    const c = adminServerStore.unusedCheck
    if (!c) return 'Unused ports and positions are checked every 3 hours.'
    if (c.running) return `Checking the servers now: ${c.done} of ${c.total} done…`
    return `Unused ports and positions are checked every 3 hours${c.finished_at ? ' · last check ' + clock(c.finished_at) : ''}${c.next_run ? ' · next ' + clock(c.next_run) : ''}`
})
const checkNow = async () => {
    checking.value = true
    await adminServerStore.checkUnused()
    // the check takes a few minutes for every server; keep the list fresh until it is done
    for (let i = 0; i < 40; i++) {
        await new Promise((resolve) => setTimeout(resolve, 8000))
        await adminServerStore.loadServers()
        if (!adminServerStore.unusedCheck?.running) break
    }
    checking.value = false
}
const { servers } = storeToRefs(adminServerStore)
const serverList = ref(null)
adminServerStore.loadServers()
const addNewOneDialogFlag = ref(false)

const openAddNewOneDialog = () => {
    addNewOneDialogFlag.value = true
}

const updateAdvanceFiltersHandler = (filters) => {
    serverList.value = servers.value.filter(server =>
        server.domain.toLowerCase().includes(filters.domain.toLowerCase()) &&
        server.isp.toLowerCase().includes(filters.isp.toLowerCase()) &&
        (server.country.toLowerCase().includes(filters.location.toLowerCase()) ||
            server.city.toLowerCase().includes(filters.location.toLowerCase())
        ) &&
        server.modemName.toLowerCase().includes(filters.modemName.toLowerCase()) &&
        server.modem_type.toLowerCase() === (filters.modemType.toLowerCase())
    )
}
watchEffect(() => {
    serverList.value = servers.value
})


</script>

<style scoped>
.region-tabs { display: flex; flex-wrap: wrap; gap: 8px; margin: 12px 0; }
.region-tab { display: inline-flex; align-items: center; gap: 8px; padding: 8px 14px; border: 1px solid var(--ui-line); border-radius: 10px; background: var(--ui-card); color: var(--ui-dim); font-size: 14px; font-weight: 600; cursor: pointer; }
.region-tab:hover, .region-tab:focus-visible { border-color: var(--ui-pri-hover); outline: none; }
.region-tab.is-on { border: 2px solid var(--ui-pri); padding: 7px 13px; background: var(--ui-pri-soft); color: var(--ui-ink); }
.region-tab__count { padding: 1px 8px; border-radius: 999px; background: var(--ui-line); color: var(--ui-ink); font-size: 12px; font-variant-numeric: tabular-nums; }
.region-tab.is-on .region-tab__count { background: var(--ui-pri); color: #ffffff; }
.unused-foot { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px; margin-top: 10px; font-size: 12.5px; color: var(--ui-dim); }
.unused-foot__btn { padding: 6px 12px; border: 1px solid var(--ui-line-strong); border-radius: 8px; background: transparent; color: var(--ui-ink); font-size: 12.5px; cursor: pointer; }
.unused-foot__btn:hover:not(:disabled) { border-color: var(--ui-pri-hover); }
.unused-foot__btn:disabled { opacity: 0.6; cursor: wait; }
</style>
